import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { faBookUser } from '@fortawesome/pro-solid-svg-icons/faBookUser';
import { faCalendarClock } from '@fortawesome/pro-solid-svg-icons/faCalendarClock';
import { faChartMixed } from '@fortawesome/pro-solid-svg-icons/faChartMixed';
import { faCircleDollar } from '@fortawesome/pro-solid-svg-icons/faCircleDollar';
import { faClipboardList } from '@fortawesome/pro-solid-svg-icons/faClipboardList';
import { faGarageCar } from '@fortawesome/pro-solid-svg-icons/faGarageCar';
import { faGear } from '@fortawesome/pro-solid-svg-icons/faGear';
import { faHome } from '@fortawesome/pro-solid-svg-icons/faHome';
import { faPercent } from '@fortawesome/pro-solid-svg-icons/faPercent';
import { faUsers } from '@fortawesome/pro-solid-svg-icons/faUsers';
import { faWarehouseFull } from '@fortawesome/pro-solid-svg-icons/faWarehouseFull';
import {
  FontAwesomeIcon,
  type FontAwesomeIconProps,
} from '@fortawesome/react-fontawesome';
import type { MenuProps } from 'antd';
import type { ItemType } from 'antd/es/menu/interface';
import { clientConfig } from 'pos-client/clientConfig.ts';
import { Logo } from 'pos-client/components/generic/logo/Logo.tsx';
import {
  NavigationKeys,
  useNavigationPermissionContext,
} from 'pos-client/contexts/navigationPermissionProvider.tsx';
import { locationAdministrationTabs } from 'pos-client/tabs/locationAdministrationTabs.ts';

import { usePermissions } from './usePermissions.tsx';
import { useCustomerFeatureFlags } from './useStaticConfig.tsx';

import sideMenuStyles from 'pos-client/layouts/app/AppNavigation.module.css';

export const useMenuItems = () => {
  const { hasAccessToRoute, isNavigationPermissionsLoading } =
    useNavigationPermissionContext();
  const { t } = useTranslation();

  const { hasPermissions } = usePermissions();
  const canAccessSettings = hasPermissions('can.access.settings');
  const canAccessRolesAndPermissions =
    hasPermissions('can.access.rolesAndPermissions') &&
    clientConfig.RolesAndPermissionsEnabled;
  const canEditUsers = hasPermissions('add.edit.users');
  const {
    endOfDayEnabled: showEndOfDay,
    cashAndBankingEnabled: showCashAndBanking,
  } = useCustomerFeatureFlags();

  const defaultIconProps: Omit<FontAwesomeIconProps, 'icon'> = useMemo(
    () => ({
      fixedWidth: true,
      style: { marginRight: 3 },
    }),
    []
  );

  const stockRoute = clientConfig.locale === 'en-US' ? 'inventory' : 'stock';

  const stockMenuItem: ItemType = useMemo(
    () => ({
      key: NavigationKeys.Stock,
      icon: (
        <FontAwesomeIcon
          icon={faWarehouseFull}
          aria-label={t('stock.stock')}
          {...defaultIconProps}
        />
      ),
      label: t('stock.stock'),
      popupClassName: `${sideMenuStyles.subMenuPopup}`, // CSS class as overrides don't affect
      children: [
        {
          key: 'items',
          label: <Link to={`/${stockRoute}/items`}>{t('items.items')}</Link>,
        },
        {
          key: 'stock-overview',
          label: (
            <Link to={`/${stockRoute}-overview/${stockRoute}-levels`}>
              {t('stock.stockOverview')}
            </Link>
          ),
        },
        {
          key: 'manage-stock-takes',
          label: (
            <Link
              to={
                stockRoute === 'inventory'
                  ? '/inventory/manage-inventory-checks/inventory-checks'
                  : '/stock/manage-stock-takes/stock-takes'
              }
            >
              {t('stock.stockTakes.stockTakes')}
            </Link>
          ),
        },
        {
          key: 'categories',
          label: (
            <Link to={`/${stockRoute}/categories`}>
              {t('categories.categories')}
            </Link>
          ),
        },
        {
          key: 'manage-suppliers',
          label: <Link to="/suppliers">{t('suppliers.suppliers')}</Link>,
        },
      ],
    }),
    [defaultIconProps, stockRoute, t]
  );

  const allSideMenuItems: MenuProps['items'] = useMemo(
    () => [
      {
        key: NavigationKeys.Home,
        icon: (
          <Logo
            style={{
              width: '18px',
              fill: 'white',
              height: 42,
              marginRight: 2,
            }}
          />
        ),
        style: { fontWeight: 'bold', fontSize: '16px' },
        label: <Link to="/">{t('siteName')}</Link>,
      },
      {
        key: NavigationKeys.PointOfSale,
        icon: (
          <FontAwesomeIcon
            icon={faHome}
            aria-label={t('common.buttons.home')}
            {...defaultIconProps}
          />
        ),
        label: (
          <Link to="/" aria-label={t('common.buttons.home')}>
            {t('common.buttons.home')}
          </Link>
        ),
      },
      {
        key: NavigationKeys.Scheduling,
        icon: (
          <FontAwesomeIcon
            icon={faCalendarClock}
            aria-label={t('scheduling.scheduling')}
            {...defaultIconProps}
          />
        ),
        label: (
          <Link to="/scheduling/schedule-board">
            {t('scheduling.scheduling')}
          </Link>
        ),
      },
      {
        key: NavigationKeys.Dashboard,
        icon: (
          <FontAwesomeIcon
            icon={faChartMixed}
            aria-label={t('dashboard.dashboard')}
            {...defaultIconProps}
          />
        ),
        label: <Link to="/dashboard/orders">{t('dashboard.dashboard')}</Link>,
      },
      {
        key: NavigationKeys.Orders,
        icon: (
          <FontAwesomeIcon
            icon={faClipboardList}
            aria-label={t('orders.orders')}
            {...defaultIconProps}
          />
        ),
        label: t('orders.orders'),
        popupClassName: `${sideMenuStyles.subMenuPopup}`, // CSS class as overrides don't affect
        children: [
          {
            key: 'Update',
            label: <Link to="/orders/manage-orders">{t('orders.orders')}</Link>,
          },
          {
            key: 'Create',
            label: <Link to="/orders/create">{t('orders.createOrder')}</Link>,
          },
        ],
      },
      {
        key: NavigationKeys.Customers,
        icon: (
          <FontAwesomeIcon
            icon={faBookUser}
            aria-label={t('customers.customers')}
            {...defaultIconProps}
          />
        ),
        label: <Link to="/customers">{t('customers.customers')}</Link>,
      },
      stockMenuItem,
      // Hidden for current time as part of ticket 29038
      // {
      //   key: NavigationKeys.Fleet,
      //   icon: (
      //     <FontAwesomeIcon
      //       icon={faCarBuilding}
      //       aria-label={t('fleet.fleet')}
      //       {...defaultIconProps}
      //     />
      //   ),
      //   label: <Link to="/fleet">{t('fleet.fleet')}</Link>,
      // },
      {
        key: NavigationKeys.Locations,
        icon: (
          <FontAwesomeIcon
            icon={faGarageCar}
            aria-label={t('locations.locations')}
            {...defaultIconProps}
          />
        ),
        label: <Link to="/locations">{t('locations.locations')}</Link>,
      },
      {
        key: NavigationKeys.Discounts,
        icon: (
          <FontAwesomeIcon
            icon={faPercent}
            aria-label={t('discounts.discounts')}
            {...defaultIconProps}
          />
        ),
        label: <Link to="/discounts">{t('discounts.discounts')}</Link>,
      },
      {
        key: NavigationKeys.StoreAccounting,
        icon: (
          <FontAwesomeIcon
            icon={faCircleDollar}
            aria-label={t('storeAccounting.storeAccounting')}
            {...defaultIconProps}
          />
        ),
        label: t('storeAccounting.storeAccounting'),
        popupClassName: `${sideMenuStyles.subMenuPopup}`,
        children: [
          ...(showEndOfDay
            ? [
                {
                  key: 'end-of-day-reporting',
                  label: (
                    <Link to="/store-accounting/end-of-day">
                      {t('storeAccounting.endOfDay')}
                    </Link>
                  ),
                },
              ]
            : []),
          ...(showCashAndBanking
            ? [
                {
                  key: 'cash-and-banking',
                  label: (
                    <Link to="/store-accounting/cash-and-banking">
                      {t('cashBanking.cashBanking')}
                    </Link>
                  ),
                },
              ]
            : []),
        ],
      },
      {
        key: NavigationKeys.Users,
        icon: (
          <FontAwesomeIcon
            icon={faUsers}
            aria-label={t('users.users')}
            {...defaultIconProps}
          />
        ),
        label: t('users.users'),
        popupClassName: `${sideMenuStyles.subMenuPopup}`, // CSS class as overrides don't affect
        children: [
          {
            key: 'manage-users',
            label: <Link to="/users">{t('users.users')}</Link>,
          },
          ...(canEditUsers
            ? [
                {
                  key: 'quality-groups',
                  label: (
                    <Link to="/quality-groups">
                      {t('users.qualityGroups.qualityGroups')}
                    </Link>
                  ),
                },
                {
                  key: 'quality-ratings',
                  label: (
                    <Link to="/quality-ratings">
                      {t('users.qualityRatings.qualityRatings')}
                    </Link>
                  ),
                },
              ]
            : []),
        ],
      },
      ...(canAccessSettings
        ? [
            {
              key: NavigationKeys.Settings,
              icon: (
                <FontAwesomeIcon
                  icon={faGear}
                  aria-label={t('settings.configurationAndIntegrations')}
                  {...defaultIconProps}
                />
              ),
              label: t('settings.settings'),
              popupClassName: `${sideMenuStyles.subMenuPopup}`,
              children: [
                {
                  key: 'platform-configuration',
                  label: (
                    <Link to={`/configuration-and-integrations`}>
                      {t('settings.configurationAndIntegrations')}
                    </Link>
                  ),
                },
                ...(canAccessRolesAndPermissions
                  ? [
                      {
                        key: 'roles-and-permissions',
                        label: (
                          <Link to={`/settings/roles-and-permissions/roles`}>
                            {t('settings.rolesAndPermissions.title')}
                          </Link>
                        ),
                      },
                    ]
                  : []),
                {
                  key: 'locations-administration',
                  label: (
                    <Link
                      to={`/settings/locations-administration/${locationAdministrationTabs.openingHoursGroup.key}`}
                    >
                      {t('settings.locationAdministration.title')}
                    </Link>
                  ),
                },
              ],
            },
          ]
        : []),
    ],
    [
      canAccessSettings,
      canAccessRolesAndPermissions,
      canEditUsers,
      defaultIconProps,
      showEndOfDay,
      showCashAndBanking,
      stockMenuItem,
      t,
    ]
  );
  const sideMenuItems = useMemo(() => {
    return allSideMenuItems
      .filter((elm) => !!elm && elm?.key)
      .filter((elm) => hasAccessToRoute(elm?.key as string));
  }, [allSideMenuItems, hasAccessToRoute]);

  return {
    isSideMenuItemsLoading: isNavigationPermissionsLoading,
    sideMenuItems,
    sideMenuItemsMobile: sideMenuItems.slice(1),
  };
};
